import React, { useState, useEffect } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import TextInput from '../../TextFields/TextInput.js';
import DateInput from '../../TextFields/DateInput.js';
import Modal from '../Modal.js';
import Text from '../../Text.js';
import Bubble from '../../Bubble.js';
import BubbleSelectBlock from '../../BubbleSelectBlock.js';
import i18n from '../../../localization/i18n.js';
import tokens from '../../../localization/tokens.js';
import * as localizationMaps from '../../../localization/maps.js';
import { genderOptions, dependentOptionsWithSpouse } from '../../../services/selectFieldOptions.js';
import validateDependentForm from './validateDependentForm.js';
import getParsedDependentData from './getParsedDependentData.js';

/**
 * 
 * @param {*} currentData 
 * @param {*} originalData 
 * @returns 
 */
const getChangedDependentData = (currentData, originalData) => {
  const changedData = {};
  if (currentData.firstname !== originalData.firstname) {
    changedData.firstname = currentData.firstname;
  }
  if (currentData.lastname !== originalData.lastname) {
    changedData.lastname = currentData.lastname;
  }
  if (localizationMaps.toEnglish('genders', currentData.gender) !== originalData.gender) {
    changedData.gender = localizationMaps.toEnglish('genders', currentData.gender);
  }
  if (dates.getUTC(currentData.birthdate) !== originalData.birthdate) {
    changedData.birthdate = dates.getUTC(currentData.birthdate);
  }
  if (currentData.email !== originalData.email) {
    changedData.email = currentData.email;
  }
  if (localizationMaps.toEnglish('relationships', currentData.relationship) !== originalData.relationship) {
    changedData.relationship = localizationMaps.toEnglish('relationships', currentData.relationship);
  }
  if (dates.getUTC(currentData.dateOfMarriage) !== originalData.dateOfMarriage && localizationMaps.toEnglish('relationships', currentData.relationship).toLowerCase() === 'spouse') {
    changedData.dateOfMarriage = dates.getUTC(currentData.dateOfMarriage);
  }
  if (
  // set the date of marriage to null if the relationship changed from spouse to common law
  localizationMaps.toEnglish('relationships', originalData.relationship).toLowerCase() === 'spouse' && localizationMaps.toEnglish('relationships', currentData.relationship).toLowerCase() === 'common law') {
    changedData.dateOfMarriage = null;
  }
  if (dates.getUTC(currentData.cohabDate) !== originalData.cohabDate && localizationMaps.toEnglish('relationships', currentData.relationship).toLowerCase() === 'common law') {
    changedData.cohabDate = dates.getUTC(currentData.cohabDate);
  }
  if (
  // set the cohab date to null if the relationship changed from common law to spouse
  localizationMaps.toEnglish('relationships', currentData.relationship).toLowerCase() === 'spouse' && localizationMaps.toEnglish('relationships', originalData.relationship).toLowerCase() === 'common law') {
    changedData.cohabDate = null;
  }
  if (currentData.hasProvincialHealthCoverage !== originalData.hasProvincialHealthCoverage) {
    changedData.hasProvincialHealthCoverage = currentData.hasProvincialHealthCoverage;
  }
  if (currentData.separateCoverage !== Boolean(originalData.insuranceCarrier || originalData.cert || originalData.employer)) {
    changedData.spouseCoverage = currentData.separateCoverage;
  }

  // if comparable coverage is toggled from yes to no, then we need to send blank data for the fields covered by the spouse benefits toggle
  if (!currentData.separateCoverage && Boolean(originalData.insuranceCarrier || originalData.cert || originalData.employer)) {
    changedData.dependentCertNumber = null;
    changedData.insuranceCarrier = null;
    changedData.employer = null;
    changedData.primaryBenefits = [];
  }
  if (currentData.employer !== originalData.employer) {
    changedData.employer = currentData.employer;
  }
  if (currentData.insuranceCarrier !== originalData.insuranceCarrier) {
    changedData.insuranceCarrier = currentData.insuranceCarrier;
  }
  if (currentData.cert !== originalData.cert) {
    changedData.dependentCertNumber = currentData.cert;
  }
  const translatedPrimaryBenefits = currentData.primaryBenefits.map(benefit => localizationMaps.toEnglish('primaryBenefits', benefit));

  /**
   * 
   * @param {*} arr1 
   * @param {*} arr2 
   * @returns 
   */
  function arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) {
      return false;
    }
    return arr1.every((value, index) => value === arr2[index]);
  }
  if (!arraysEqual(translatedPrimaryBenefits, originalData.primaryBenefits)) {
    changedData.primaryBenefits = translatedPrimaryBenefits;
  }
  return changedData;
};

/**
 * 
 * @returns 
 */
const primaryBenefitsNames = () => [i18n.t(tokens.dropdowns.categoryOptions.drugs), i18n.t(tokens.dropdowns.categoryOptions.dental), i18n.t(tokens.titles.majorMedical), i18n.t(tokens.dropdowns.categoryOptions.vision), i18n.t(tokens.titles.outOfCountryAndProvince), i18n.t(tokens.titles.paramedical)];

/**
 * 
 * @param {*} dependentData 
 * @param {*} isEdit 
 * @returns 
 */
const setDependentData = (dependentData, isEdit) => ({
  firstname: isEdit && dependentData ? dependentData.firstname : '',
  lastname: isEdit && dependentData ? dependentData.lastname : '',
  gender: isEdit && dependentData ? localizationMaps.fromEnglish("genders", dependentData.gender) : localizationMaps.fromEnglish("genders", 'Female'),
  birthdate: isEdit && dependentData ? dates.formatIncomingDate(dependentData.birthdate, 'YYYY/MM/DD') : '',
  email: isEdit && dependentData ? dependentData.email : '',
  relationship: isEdit && dependentData ? localizationMaps.fromEnglish("relationships", dependentData.relationship) : localizationMaps.fromEnglish("relationships", 'Child'),
  dateOfMarriage: isEdit && dependentData?.dateOfMarriage ? dates.formatIncomingDate(dependentData.birthdate, 'YYYY/MM/DD') : null,
  cohabDate: isEdit && dependentData?.cohabDate ? dates.formatIncomingDate(dependentData.cohabDate, 'YYYY/MM/DD') : null,
  hasProvincialHealthCoverage: isEdit && dependentData ? dependentData.hasProvincialHealthCoverage : false,
  employer: isEdit && dependentData ? dependentData.employer : '',
  insuranceCarrier: isEdit && dependentData ? dependentData.insuranceCarrier : '',
  cert: isEdit && dependentData ? dependentData.cert : '',
  primaryBenefits: isEdit && dependentData ? dependentData.primaryBenefits.map(benefit => localizationMaps.fromEnglish('primaryBenefits', benefit)) : [],
  separateCoverage: isEdit && dependentData ? Boolean(dependentData.insuranceCarrier || dependentData.cert || dependentData.employer) : false
});

/**
 * 
 * @returns 
 */
const initialErrorState = {
  firstnameError: false,
  lastnameError: false,
  genderError: false,
  birthdateError: false,
  emailError: false,
  relationshipError: false,
  dateOfMarriageError: false,
  cohabDateError: false,
  spousesOrganizationError: false,
  insuranceCarrierError: false,
  certError: false
};

/**
 * 
 * @returns 
 */
const DependentModal = ({
  open,
  dependentData,
  goBack,
  isEdit,
  openChangeRequestModal,
  whitelabel,
  loader
}) => {
  const [dependent, setDependent] = useState(setDependentData(dependentData, isEdit));
  const [errorState, setErrorState] = useState(initialErrorState);
  const styles = {
    bubbleListSelector: {
      margin: '10px 0',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px'
    },
    bubble: {
      fontSize: '12px',
      padding: '4px 13px',
      minWidth: '0',
      margin: '0'
    }
  };

  // set the dependent info in state when the component mounts
  useEffect(() => {
    setDependent(setDependentData(dependentData, isEdit));
  }, [dependentData, isEdit]);
  const {
    firstname,
    lastname,
    gender,
    birthdate,
    email,
    relationship,
    dateOfMarriage,
    cohabDate,
    hasProvincialHealthCoverage,
    separateCoverage,
    employer,
    insuranceCarrier,
    cert,
    primaryBenefits
  } = dependent;
  const {
    firstnameError,
    lastnameError,
    genderError,
    birthdateError,
    emailError,
    relationshipError,
    dateOfMarriageError,
    cohabDateError,
    employerError,
    insuranceCarrierError,
    certError
  } = errorState;

  /**
   * Closes the modal and resets form states.
   */
  const close = () => {
    setDependent(setDependentData(dependentData, false));
    setErrorState(initialErrorState);
    goBack();
  };

  /**
   * 
   * @param {*} field 
   * @param {*} value 
   */
  const _handleInputChange = (field, value) => {
    setDependent(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /**
   * 
   * @param {*} option 
   */
  const handleBubbleListSelector = option => {
    const selection = [...primaryBenefits];
    const index = selection.findIndex(e => e === option);
    if (index === -1) {
      selection.push(option);
    } else {
      selection.splice(index, 1);
    }
    _handleInputChange('primaryBenefits', selection);
  };

  /**
   * 
   * @returns 
   */
  const validateFormAndSetErrors = async () => {
    const formErrors = await validateDependentForm(dependent);
    if (!formErrors) {
      return true;
    }
    setErrorState({
      ...formErrors
    });
    return false;
  };

  /**
   * 
   * @returns 
   */
  const saveDependent = async () => {
    try {
      const isFormValid = await validateFormAndSetErrors();
      if (!isFormValid) {
        return false;
      }
      const changeRequestDetails = isEdit ? getChangedDependentData(dependent, dependentData) : getParsedDependentData(dependent);
      if (Object.keys(changeRequestDetails).length === 0) {
        // No data has changed
        return false;
      }
      const changeRequestData = {
        type: isEdit ? 'Edit Dependent Details' : 'Add a Dependent',
        details: {
          ...(isEdit ? {
            dependentID: dependentData.dependentID
          } : {}),
          ...changeRequestDetails
        }
      };
      const success = await openChangeRequestModal(changeRequestData);
      if (success) {
        close();
      }
      return false;
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('Error in saveDependent:', error);
      return false;
    }
  };
  return /*#__PURE__*/React.createElement(Modal, {
    open: open,
    title: !isEdit ? i18n.t(tokens.titles['add dependent']) : i18n.t(tokens.titles["edit dependent"]),
    medium: true,
    primaryButton: {
      label: i18n.t(tokens.buttons.saveDependent),
      onClick: saveDependent
    },
    goBack: goBack,
    onClose: close,
    loader: loader
  }, /*#__PURE__*/React.createElement(Text, {
    marginTop: 20
  }, i18n.t(tokens.labels.dependents.validChildNote)), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.firstname),
    value: firstname,
    onChange: e => _handleInputChange('firstname', e.target.value),
    error: firstnameError,
    whitelabel: whitelabel
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.lastname),
    value: lastname,
    onChange: e => _handleInputChange('lastname', e.target.value),
    error: lastnameError,
    whitelabel: whitelabel
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.gender),
    value: gender,
    onChange: e => _handleInputChange('gender', e.target.value),
    select: true,
    selectOptions: genderOptions(),
    error: genderError,
    whitelabel: whitelabel
  }), /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.birthdate),
    value: birthdate,
    onChange: e => _handleInputChange('birthdate', e.target.value),
    error: birthdateError,
    whitelabel: whitelabel
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.account.emailOptional),
    value: email,
    onChange: e => _handleInputChange('email', e.target.value),
    error: emailError,
    whitelabel: whitelabel
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.relationship),
    value: relationship,
    onChange: e => _handleInputChange('relationship', e.target.value),
    select: true,
    selectOptions: dependentOptionsWithSpouse(),
    error: relationshipError,
    whitelabel: whitelabel
  }), localizationMaps.toEnglish("relationships", relationship).toLowerCase() === "spouse" ? /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.dependents.marriageDate),
    value: dateOfMarriage,
    onChange: e => _handleInputChange('dateOfMarriage', e.target.value),
    error: dateOfMarriageError,
    whitelabel: whitelabel
  }) : null, localizationMaps.toEnglish("relationships", relationship).toLowerCase() === "common law" ? /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.dependents.commonLawCohabDate),
    value: cohabDate,
    onChange: e => _handleInputChange('cohabDate', e.target.value),
    error: cohabDateError,
    whitelabel: whitelabel
  }) : null, /*#__PURE__*/React.createElement(Text, null, i18n.t(tokens.labels.dependents.doTheyHaveProvincialHealthCoverage)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
    first: {
      title: i18n.t(tokens.labels.yes),
      selected: hasProvincialHealthCoverage
    },
    second: {
      title: i18n.t(tokens.labels.no),
      selected: !hasProvincialHealthCoverage
    },
    handleChange: () => _handleInputChange("hasProvincialHealthCoverage", !hasProvincialHealthCoverage),
    whitelabel: whitelabel
  }), localizationMaps.toEnglish("relationships", relationship).toLowerCase() === "common law" || localizationMaps.toEnglish("relationships", relationship).toLowerCase() === "spouse" ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, null, i18n.t(tokens.labels.changeRequests.doesThisDependentHaveComparableCoverage)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
    first: {
      title: i18n.t(tokens.labels.yes),
      selected: separateCoverage
    },
    second: {
      title: i18n.t(tokens.labels.no),
      selected: !separateCoverage
    },
    handleChange: () => _handleInputChange("separateCoverage", !separateCoverage),
    whitelabel: whitelabel
  })) : null, separateCoverage ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.dependents.dependentOrganization),
    value: employer,
    onChange: e => _handleInputChange('employer', e.target.value),
    error: employerError,
    whitelabel: whitelabel
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.dependents.dependentCurrentInsuranceCarrier),
    value: insuranceCarrier,
    onChange: e => _handleInputChange('insuranceCarrier', e.target.value),
    error: insuranceCarrierError,
    whitelabel: whitelabel
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.dependents.dependentCertificateNumber),
    value: cert,
    onChange: e => _handleInputChange('cert', e.target.value),
    error: certError,
    whitelabel: whitelabel
  }), /*#__PURE__*/React.createElement(Text, {
    medium: true,
    light: true,
    margin: "10px 0 10px 0"
  }, i18n.t(tokens.labels.changeRequests.pleaseIndicateBenefit)), /*#__PURE__*/React.createElement("div", {
    style: styles.bubbleListSelector
  }, primaryBenefitsNames().map(option => /*#__PURE__*/React.createElement(Bubble, {
    customStyle: styles.bubble,
    key: option,
    label: option,
    isSelected: primaryBenefits?.includes(option),
    onClick: () => handleBubbleListSelector(option)
  })))) : null);
};
export default DependentModal;